
import React from 'react';
import MainLayout from '@/components/Layout/MainLayout';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <MainLayout>
      {/* Hero Section */}
      <section className="bg-forti-fi-background py-16 px-4" aria-labelledby="privacy-hero-heading">
        <div className="container mx-auto max-w-4xl">
          <h1 id="privacy-hero-heading" className="text-forti-fi mb-6 text-center">Privacy Policy</h1>
          <div className="flex items-center justify-center mb-8 text-forti-fi">
            <Shield className="h-10 w-10 mr-3" />
            <p className="text-xl font-serif">Your privacy is our commitment</p>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="prose prose-lg prose-stone mx-auto">
            <p className="text-lg mb-6">Last Updated: {new Date().toLocaleDateString('en-CA')}</p>
            
            <h2 className="text-forti-fi">Introduction</h2>
            <p>
              Forti-Fi ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how your personal information is collected, used, and disclosed by Forti-Fi when you use our website, products, or services.
            </p>
            <p>
              By accessing or using our services, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Privacy Policy and our Terms of Service.
            </p>
            
            <h2 className="text-forti-fi">Information We Collect</h2>
            <p>
              We collect information that you provide directly to us when you:
            </p>
            <ul>
              <li>Request a consultation or demonstration</li>
              <li>Contact our sales or support teams</li>
              <li>Subscribe to our newsletter</li>
              <li>Participate in surveys or promotions</li>
              <li>Apply for employment</li>
            </ul>
            <p>
              This information may include your name, company name, email address, telephone number, mailing address, and any other information you choose to provide.
            </p>
            
            <h2 className="text-forti-fi">How We Use Your Information</h2>
            <p>
              We use the information we collect for various purposes, including to:
            </p>
            <ul>
              <li>Provide, maintain, and improve our services</li>
              <li>Process and complete transactions</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Communicate with you about products, services, offers, and events</li>
              <li>Provide news and information that may be of interest to you</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
              <li>Comply with our legal and regulatory obligations</li>
            </ul>
            
            <h2 className="text-forti-fi">Disclosure of Information</h2>
            <p>
              We maintain the strictest confidentiality standards in all client engagements and do not share your personal information with third parties except as described in this Privacy Policy.
            </p>
            <p>
              We may share your personal information with:
            </p>
            <ul>
              <li>Service providers who perform services on our behalf</li>
              <li>Professional advisors, such as lawyers, auditors, and insurers</li>
              <li>Government and regulatory authorities, as required by law</li>
            </ul>
            <p>
              We require all third parties to respect the security of your personal information and to treat it in accordance with the law.
            </p>
            
            <h2 className="text-forti-fi">Data Security</h2>
            <p>
              We implement appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
            </p>
            
            <h2 className="text-forti-fi">Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>
            
            <h2 className="text-forti-fi">Your Rights and Choices</h2>
            <p>
              Under Ontario privacy laws, you have certain rights regarding your personal information, including:
            </p>
            <ul>
              <li>The right to access your personal information</li>
              <li>The right to correct inaccurate information</li>
              <li>The right to withdraw consent</li>
              <li>The right to file a complaint with the Privacy Commissioner of Ontario</li>
            </ul>
            <p>
              To exercise these rights, please contact us at privacy@forti-fi.ca.
            </p>
            
            <h2 className="text-forti-fi">Changes to this Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes to our practices or for other operational, legal, or regulatory reasons. We will post the revised Privacy Policy on our website with the effective date.
            </p>
            
            <h2 className="text-forti-fi">Contact Us</h2>
            <p>
              If you have any questions or concerns about our Privacy Policy or our handling of your personal information, please contact us at:
            </p>
            <div className="my-4">
              <p className="mb-1"><strong>Email:</strong> privacy@forti-fi.ca</p>
              <p className="mb-1"><strong>Address:</strong> Forti-Fi, Ontario, Canada</p>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Button asChild variant="gradient" className="text-white">
              <Link to="/contact">Contact Us About Privacy</Link>
            </Button>
          </div>
        </div>
      </section>
    </MainLayout>
  );
};

export default PrivacyPolicy;
